import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiService } from '../../api';
import { PaginatedResponse, SortOrder } from '../../common/entities/paginated-response';
import { ARRIVAL_BOARD_CONFIG_PAGE_SIZE, ArrivalBoardConfigService } from './arrival-board-config.service';
import { ArrivalBoardConfiguration } from '../entities/arrival-board-configuration';
import { joinUrl } from './join-url';

const CONFIGURATIONS_PATH = 'arrival-board/configurations';

/** The backend expects the enum key (`NAME`), not the column name. */
export enum ArrivalBoardConfigSortBy {
    UUID = 'UUID',
    NAME = 'NAME',
    GROUP_PATH = 'GROUP_PATH',
    QUESTIONNAIRE = 'QUESTIONNAIRE',
    CREATED_AT = 'CREATED_AT',
    UPDATED_AT = 'UPDATED_AT',
}

/** The server clamps `pollingInterval`, so every write returns what was actually persisted. */
@Service({ autoProvided: false })
export class HttpArrivalBoardConfigService extends ArrivalBoardConfigService {
    private readonly http = inject(HttpClient);
    private readonly apiService = inject(ApiService);

    getConfigs(
        offset = 0,
        limit = ARRIVAL_BOARD_CONFIG_PAGE_SIZE,
        sortBy: ArrivalBoardConfigSortBy = ArrivalBoardConfigSortBy.NAME,
        sortOrder: SortOrder = SortOrder.ASC,
    ): Observable<PaginatedResponse<ArrivalBoardConfiguration[]>> {
        const params = new HttpParams()
            .set('limit', limit)
            .set('offset', offset)
            .set('sortBy', sortBy)
            .set('sortOrder', sortOrder);
        return this.http
            .get<PaginatedResponse<ArrivalBoardConfiguration[]>>(this.configurationsUrl(), { params })
            .pipe(map((response) => ({ ...response, items: response?.items ?? [], total: response?.total ?? 0 })));
    }

    getConfig(uuid: string): Observable<ArrivalBoardConfiguration | undefined> {
        return this.http.get<ArrivalBoardConfiguration>(`${this.configurationsUrl()}/${uuid}`);
    }

    create(config: ArrivalBoardConfiguration): Observable<ArrivalBoardConfiguration> {
        return this.http.post<ArrivalBoardConfiguration>(this.configurationsUrl(), writeBody(config));
    }

    update(config: ArrivalBoardConfiguration): Observable<ArrivalBoardConfiguration> {
        return this.http.put<ArrivalBoardConfiguration>(
            `${this.configurationsUrl()}/${config.uuid}`,
            writeBody(config),
        );
    }

    delete(uuid: string): Observable<void> {
        return this.http.delete<void>(`${this.configurationsUrl()}/${uuid}`);
    }

    /** Resolved per call: a field would freeze the URL before `ApiService.init()` has resolved the root. */
    private configurationsUrl(): string {
        const href = this.apiService.rootResponseSignal()?._links?.arrivalBoardConfiguration?.href;
        return joinUrl(this.apiService.urlSignal(), href ?? CONFIGURATIONS_PATH);
    }
}

/**
 * Explicit property list: the backend runs `forbidNonWhitelisted`, so spreading a persisted resource
 * (`_links`, timestamps) is a hard `400`.
 */
function writeBody(config: ArrivalBoardConfiguration): Record<string, unknown> {
    return {
        name: config.name,
        pollingInterval: config.pollingInterval,
        questionnaire: config.questionnaire,
        columns: config.columns,
        // ⚠ Always sent, `[]` included: the backend update is a partial merge, so an omitted `alerts`
        // would keep the deleted last rule.
        alerts: config.alerts,
        etaRetentionMinutes: config.etaRetentionMinutes,
    };
}
